import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';

export default function AdminDashboard() {
  const { api, navigate } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [feed, setFeed] = useState(null);
  const [showFeed, setShowFeed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [feedError, setFeedError] = useState(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const data = await api('/admin/dashboard');
      // If this fails, say so on the page. Hiding it made the whole section
      // vanish with no explanation, which looked exactly like the code never
      // having been published.
      api('/admin/google-conversions')
        .then(setFeed)
        .catch((err) => setFeedError(err.message || 'Could not load the Google Ads section'));
      setDashboardData(data);
    } catch (error) {
      console.error('Failed to load admin dashboard:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-coral-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const { stats, recentLeads, leadDemand } = dashboardData;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-display text-3xl text-navy-900 mb-2">Admin Dashboard</h1>
        <p className="text-navy-600 font-body">Overview of the lead distribution program</p>
      </div>

      {/* Lead Demand Card */}
      {leadDemand && (
        <div className="card mb-8 bg-gradient-to-r from-coral-50 to-coral-100 border border-coral-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-display text-xl text-navy-900 mb-1">Lead Demand This Week</h2>
              <p className="text-navy-600 font-body text-sm">
                Based on {leadDemand.activeAdvisors} active advisor{leadDemand.activeAdvisors !== 1 ? 's' : ''} with leads in rotation
              </p>
            </div>
            <div className="text-right">
              <p className="text-4xl font-display text-coral-600">{leadDemand.leadsNeeded}</p>
              <p className="text-sm text-navy-600 font-body">leads needed</p>
            </div>
          </div>
          {leadDemand.breakdown && leadDemand.breakdown.length > 0 && (
            <div className="mt-4 pt-4 border-t border-coral-200">
              <p className="text-xs text-navy-500 uppercase tracking-wide mb-2">Breakdown by Advisor</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {leadDemand.breakdown.slice(0, 8).map((item, index) => (
                  <div key={index} className="bg-white rounded px-3 py-2 text-sm">
                    <span className="text-navy-900 font-medium">{item.name}</span>
                    <span className="text-navy-500 ml-1">({item.leadsPerWeek}/wk)</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <span className="text-navy-500 text-sm uppercase tracking-wide font-body">Total Advisors</span>
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
          </div>
          <p className="text-3xl font-display text-navy-900">{stats.totalAdvisors}</p>
          <p className="text-navy-500 text-sm mt-1 font-body">registered advisors</p>
        </div>

        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <span className="text-navy-500 text-sm uppercase tracking-wide font-body">Active in Rotation</span>
            <div className="w-10 h-10 bg-highlight-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-highlight-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <p className="text-3xl font-display text-navy-900">{stats.activeAdvisors}</p>
          <p className="text-navy-500 text-sm mt-1 font-body">receiving leads</p>
        </div>

        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <span className="text-navy-500 text-sm uppercase tracking-wide font-body">Total Leads</span>
            <div className="w-10 h-10 bg-coral-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-coral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
          </div>
          <p className="text-3xl font-display text-navy-900">{stats.totalLeads}</p>
          <div className="flex gap-3 mt-1">
            <span className="text-highlight-600 text-sm font-body">{stats.assignedLeads} assigned</span>
            {stats.pendingLeads > 0 && (
              <span className="text-amber-600 text-sm font-body">{stats.pendingLeads} pending</span>
            )}
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <span className="text-navy-500 text-sm uppercase tracking-wide font-body">Total Revenue</span>
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
          <p className="text-3xl font-display text-navy-900">${(stats.totalRevenue / 100).toLocaleString()}</p>
          <p className="text-navy-500 text-sm mt-1 font-body">from lead packages</p>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <div className="card flex items-center justify-between">
          <div>
            <h3 className="font-medium text-navy-900">Paused Advisors</h3>
            <p className="text-navy-500 text-sm font-body">{stats.pausedAdvisors} advisors have paused delivery</p>
          </div>
          <span className="text-2xl font-display text-amber-600">{stats.pausedAdvisors}</span>
        </div>

        <div className="card flex items-center justify-between">
          <div>
            <h3 className="font-medium text-navy-900">Pending Leads</h3>
            <p className="text-navy-500 text-sm font-body">Leads awaiting assignment</p>
          </div>
          <span className={`text-2xl font-display ${stats.pendingLeads > 0 ? 'text-amber-600' : 'text-highlight-600'}`}>
            {stats.pendingLeads}
          </span>
        </div>
      </div>

      {/* Recent Leads */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-xl text-navy-900">Recent Leads</h2>
          <button onClick={() => navigate('/admin/leads')} className="text-coral-600 hover:text-coral-700 text-sm font-medium">
            View All →
          </button>
        </div>

        {recentLeads.length === 0 ? (
          <div className="text-center py-8 text-navy-500 font-body">
            No leads submitted yet
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-cream-200">
                  <th className="text-left text-xs font-medium text-navy-500 uppercase tracking-wider py-3 px-4">Lead</th>
                  <th className="text-left text-xs font-medium text-navy-500 uppercase tracking-wider py-3 px-4">Contact</th>
                  <th className="text-left text-xs font-medium text-navy-500 uppercase tracking-wider py-3 px-4">Assigned To</th>
                  <th className="text-left text-xs font-medium text-navy-500 uppercase tracking-wider py-3 px-4">Status</th>
                  <th className="text-left text-xs font-medium text-navy-500 uppercase tracking-wider py-3 px-4">Date</th>
                </tr>
              </thead>
              <tbody>
                {recentLeads.map((lead) => (
                  <tr key={lead.id} className="border-b border-cream-100 hover:bg-cream-50">
                    <td className="py-4 px-4">
                      <p className="font-medium text-navy-900">{lead.name}</p>
                    </td>
                    <td className="py-4 px-4">
                      <a href={`mailto:${lead.email}`} className="text-coral-600 hover:text-coral-700 text-sm">
                        {lead.email}
                      </a>
                    </td>
                    <td className="py-4 px-4">
                      {lead.first_name ? (
                        <span className="text-navy-900">{lead.first_name} {lead.last_name}</span>
                      ) : (
                        <span className="text-navy-400">Unassigned</span>
                      )}
                    </td>
                    <td className="py-4 px-4">
                      <span className={`badge ${lead.status === 'assigned' ? 'badge-success' : 'badge-warning'}`}>
                        {lead.status}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-navy-500 text-sm font-body">
                      {new Date(lead.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Permission to text */}
      {dashboardData?.smsConsent?.asked > 0 && (
        <div className="card mt-8">
          <h2 className="font-display text-xl text-navy-900">Permission to text</h2>
          <p className="text-navy-600 text-sm mt-1 mb-4">
            Only people who ticked the box may be texted, and only they can come to you
            under the split.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-2xl font-display text-navy-900">
                {Math.round(dashboardData.smsConsent.rate * 100)}%
              </p>
              <p className="text-xs text-navy-500">said yes</p>
            </div>
            <div>
              <p className="text-2xl font-display text-navy-900">
                {dashboardData.smsConsent.agreed}
              </p>
              <p className="text-xs text-navy-500">
                of {dashboardData.smsConsent.asked} who were asked
              </p>
            </div>
            <div>
              <p className="text-2xl font-display text-navy-900">
                {dashboardData.smsConsent.last30.asked > 0
                  ? Math.round((dashboardData.smsConsent.last30.agreed / dashboardData.smsConsent.last30.asked) * 100) + '%'
                  : '--'}
              </p>
              <p className="text-xs text-navy-500">last 30 days</p>
            </div>
            <div>
              <p className={`text-2xl font-display ${
                dashboardData.smsConsent.advisorsConsentedShare >= 0.5
                  ? 'text-navy-900'
                  : 'text-coral-600'
              }`}>
                {Math.round(dashboardData.smsConsent.advisorsConsentedShare * 100)}%
              </p>
              <p className="text-xs text-navy-500">of the advisors' half would have said yes</p>
            </div>
          </div>

          {/* The number that actually decides whether the split is fair */}
          <div className={`mt-5 rounded-lg p-4 text-sm ${
            dashboardData.smsConsent.advisorsConsentedShare >= 0.5
              ? 'bg-highlight-50 border border-highlight-200 text-navy-700'
              : 'bg-cream-100 border border-cream-300 text-navy-700'
          }`}>
            {dashboardData.smsConsent.asked < 30 ? (
              <>
                <strong>Too early to read.</strong> Only {dashboardData.smsConsent.asked}{' '}
                {dashboardData.smsConsent.asked === 1 ? 'person has' : 'people have'} been
                asked so far. Wait until around 30 before drawing any conclusion from this.
              </>
            ) : dashboardData.smsConsent.advisorsConsentedShare >= 0.5 ? (
              <>
                <strong>The split works at this rate.</strong> You can take half of every
                lead and the advisors still get a real mix.
              </>
            ) : dashboardData.smsConsent.advisorsConsentedShare > 0 ? (
              <>
                <strong>The advisors' half is thinning out.</strong> If you keep taking half
                of every lead, only{' '}
                {Math.round(dashboardData.smsConsent.advisorsConsentedShare * 100)}% of what
                they receive will be people who agreed to be contacted. Worth considering
                taking a smaller share.
              </>
            ) : (
              <>
                <strong>The advisors would get nobody who said yes.</strong> At this rate you
                cannot take half of every lead without leaving them only people who declined.
                Take a smaller share instead.
              </>
            )}
          </div>
        </div>
      )}

      {/* Google Ads conversion feed */}
      {feedError && (
        <div className="card mt-8 border border-red-200 bg-red-50">
          <h2 className="font-display text-xl text-navy-900 mb-1">Google Ads</h2>
          <p className="text-navy-700 text-sm">
            This section could not load: {feedError}
          </p>
        </div>
      )}

      {feed && (
        <div className="card mt-8">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h2 className="font-display text-xl text-navy-900">Google Ads</h2>
              <p className="text-navy-600 text-sm mt-1">
                What we send back to Google so it stops chasing people who only fill in forms.
              </p>
            </div>
            <button onClick={() => setShowFeed(!showFeed)} className="text-coral-600 hover:text-coral-700 text-sm font-medium whitespace-nowrap">
              {showFeed ? 'Hide' : 'Set up'}
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-2">
            <div>
              <p className="text-2xl font-display text-navy-900">{feed.counts.leadsWithClickId}</p>
              <p className="text-xs text-navy-500">leads traced to an ad click</p>
            </div>
            <div>
              <p className="text-2xl font-display text-navy-900">{feed.counts.goodLeads}</p>
              <p className="text-xs text-navy-500">counted as a good lead</p>
            </div>
            <div>
              <p className="text-2xl font-display text-navy-900">{feed.counts.bookedMeetings}</p>
              <p className="text-xs text-navy-500">booked meetings</p>
            </div>
            <div>
              <p className="text-2xl font-display text-navy-400">{feed.counts.leadsWithoutClickId}</p>
              <p className="text-xs text-navy-500">no ad click recorded</p>
            </div>
          </div>
          <p className="text-xs text-navy-400">Last {feed.windowDays} days.</p>

          {showFeed && (
            <div className="mt-6 pt-6 border-t border-cream-200 space-y-6">
              <div>
                <p className="font-medium text-navy-900 mb-1">1. Create these two conversion actions in Google Ads</p>
                <p className="text-sm text-navy-600 mb-3">
                  Goals &rarr; Conversions &rarr; New conversion action &rarr; Import &rarr; Manual import using API or uploads.
                  The names must match exactly:
                </p>
                <ul className="space-y-2 text-sm">
                  <li>
                    <code className="bg-cream-100 px-2 py-1 rounded">{feed.conversionNames.lead}</code>
                    <span className="text-navy-600 ml-2">every lead, for cost per lead. Set to <strong>Secondary</strong>.</span>
                  </li>
                  <li>
                    <code className="bg-cream-100 px-2 py-1 rounded">{feed.conversionNames.goodLead}</code>
                    <span className="text-navy-600 ml-2">worth bidding on. Set to <strong>Primary</strong>.</span>
                  </li>
                  <li>
                    <code className="bg-cream-100 px-2 py-1 rounded">{feed.conversionNames.meeting}</code>
                    <span className="text-navy-600 ml-2">Secondary for now, Primary once a few weeks have built up.</span>
                  </li>
                </ul>
                <p className="text-sm text-navy-600 mt-3">
                  Then set your existing form-fill conversions to <strong>Secondary</strong>. While they stay
                  Primary, Google keeps chasing form fills and none of this changes anything.
                </p>
              </div>

              <div>
                <p className="font-medium text-navy-900 mb-1">2. Connect the file in Data Manager</p>
                <p className="text-sm text-navy-600 mb-3">
                  Goals &rarr; Conversions &rarr; Uploads &rarr; Schedules &rarr; Go to Data Manager.
                  Add an <strong>HTTPS</strong> connection and give it these three things.
                </p>

                {[
                  { label: 'File address', value: feed.url, id: 'url' },
                  { label: 'Username', value: feed.username, id: 'user' },
                  { label: 'Password', value: feed.password, id: 'pass' }
                ].map((row) => (
                  <div key={row.id} className="mb-2">
                    <label className="text-xs text-navy-500 uppercase tracking-wide">{row.label}</label>
                    <div className="flex gap-2 mt-1">
                      <input
                        readOnly
                        value={row.value}
                        onFocus={(e) => e.target.select()}
                        className="flex-1 bg-cream-50 border border-cream-200 rounded px-3 py-2 text-xs font-mono text-navy-700"
                      />
                      <button
                        onClick={() => {
                          navigator.clipboard?.writeText(row.value);
                          setCopied(row.id);
                          setTimeout(() => setCopied(false), 2000);
                        }}
                        className="btn-secondary whitespace-nowrap"
                      >
                        {copied === row.id ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                  </div>
                ))}

                <p className="text-xs text-navy-500 mt-2">
                  Keep the password private. Anyone with it can read your lead tracking codes.
                </p>
                <p className="text-xs text-navy-500 mt-1">
                  To see the file yourself,{' '}
                  <a href={feed.testUrl} target="_blank" rel="noopener noreferrer" className="text-coral-600 underline">
                    open it in a browser
                  </a>.
                </p>
              </div>

              {feed.recent.length > 0 && (
                <div>
                  <p className="font-medium text-navy-900 mb-2">3. What would be sent right now</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-cream-200 text-xs text-navy-500 uppercase tracking-wide">
                          <th className="text-left py-2 pr-4">Lead</th>
                          <th className="text-left py-2 pr-4">Budget</th>
                          <th className="text-left py-2 pr-4">Dates</th>
                          <th className="text-left py-2 pr-4">Score</th>
                          <th className="text-left py-2">Sent as</th>
                        </tr>
                      </thead>
                      <tbody>
                        {feed.recent.map((l) => (
                          <tr key={l.id} className="border-b border-cream-100">
                            <td className="py-2 pr-4 text-navy-900">{l.name}</td>
                            <td className="py-2 pr-4 text-navy-600">{l.budget || '--'}</td>
                            <td className="py-2 pr-4 text-navy-600">{l.travel_dates || '--'}</td>
                            <td className="py-2 pr-4 text-navy-600">{l.score}/10</td>
                            <td className="py-2">
                              <span className="badge badge-info mr-1">Lead</span>
                              {l.isGood && <span className="badge badge-success mr-1">Good lead</span>}
                              {l.meeting_booked && <span className="badge badge-success">Meeting</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Database backup */}
      <div className="card mt-8">
        <h2 className="font-display text-xl text-navy-900 mb-1">Backup</h2>
        <p className="text-navy-600 text-sm mb-4">
          Every lead, every advisor and every lead balance lives in one file on one machine.
          This downloads a copy of it.
        </p>
        <a href="/api/admin/backup" className="btn-primary inline-block">
          Download a backup
        </a>
        <p className="text-xs text-navy-500 mt-3">
          Keep it somewhere safe, such as Dropbox. It holds every lead's name, email and phone
          number, so treat it as the customer list it is.
        </p>
      </div>

    </div>
  );
}
